/**
 * shared/api/query — Typed query/mutation helpers over TanStack Query.
 *
 * Provides factory functions that marry openapi-fetch typed calls with
 * TanStack Query's useQuery / useMutation hooks.  Response types are
 * inferred from schema.d.ts — no manual type annotations needed at call sites.
 *
 * Pattern:
 *   // In a slice's api/ file:
 *   export const useHealthQuery = createGetQuery('/api/health/', ['health']);
 *
 *   // In a component:
 *   const { data, isLoading } = useHealthQuery();
 *   // data is typed as { status: string } | undefined
 *
 * No feature-specific logic lives here.
 * No imports from layers above shared — this is the bottom FSD layer.
 */

import {
  useQuery,
  useMutation,
  type UseQueryOptions,
  type UseMutationOptions,
  type QueryKey,
} from '@tanstack/react-query';
import type { paths } from './schema';
import { apiClient } from './client';

// ---------------------------------------------------------------------------
// Type utilities derived from the schema
// ---------------------------------------------------------------------------

/**
 * Extracts the 200-response JSON body type for a given GET path.
 * Falls back to `unknown` when the path has no typed 200 response.
 */
type GetResponseData<P extends keyof paths> = paths[P] extends {
  get: { responses: { 200: { content: { 'application/json': infer T } } } };
}
  ? T
  : unknown;

/**
 * Extracts the request body JSON type for a given POST path.
 * Falls back to `never` when the path has no typed request body.
 */
type PostRequestBody<P extends keyof paths> = paths[P] extends {
  post: { requestBody: { content: { 'application/json': infer T } } };
}
  ? T
  : never;

/**
 * Extracts the 200/201-response JSON body type for a given POST path.
 * Falls back to `unknown` when the path has no typed response.
 */
type PostResponseData<P extends keyof paths> = paths[P] extends {
  post: { responses: { 200: { content: { 'application/json': infer T } } } };
}
  ? T
  : paths[P] extends {
        post: { responses: { 201: { content: { 'application/json': infer T } } } };
      }
    ? T
    : unknown;

// ---------------------------------------------------------------------------
// GET query helper
// ---------------------------------------------------------------------------

/**
 * Options for a typed GET query.
 * Omit queryKey/queryFn — they are managed by the factory.
 */
export type TypedQueryOptions<TData> = Omit<
  UseQueryOptions<TData, Error, TData, QueryKey>,
  'queryKey' | 'queryFn'
>;

/**
 * Creates a typed useQuery hook for a GET endpoint.
 *
 * @param path    - The OpenAPI path key (e.g. '/api/health/')
 * @param key     - The TanStack Query cache key array
 * @param options - Optional extra UseQueryOptions (staleTime, enabled, etc.)
 *
 * @example
 *   export const useHealthQuery = createGetQuery('/api/health/', ['health']);
 */
export function createGetQuery<P extends keyof paths>(
  path: P,
  key: QueryKey,
  options?: TypedQueryOptions<GetResponseData<P>>,
) {
  type TData = GetResponseData<P>;

  return function useTypedQuery(extraOptions?: TypedQueryOptions<TData>) {
    return useQuery<TData, Error>({
      queryKey: key,
      queryFn: async (): Promise<TData> => {
        // We cast apiClient.GET to accept any path string so TypeScript doesn't
        // error on the dynamic path parameter — the schema type-checking happens
        // at the factory call site via the P generic.
        const result = await (apiClient.GET as (p: string) => Promise<{ data?: unknown; error?: unknown; response: Response }>)(path as string);
        if (result.error !== undefined) {
          const err = result.error;
          const detail =
            typeof err === 'object' && err !== null && 'detail' in err
              ? String((err as Record<string, unknown>)['detail'])
              : `API error: ${result.response.status}`;
          throw new Error(detail);
        }
        return result.data as TData;
      },
      ...options,
      ...extraOptions,
    });
  };
}

// ---------------------------------------------------------------------------
// POST mutation helper
// ---------------------------------------------------------------------------

/**
 * Options for a typed POST mutation.
 * Omit mutationFn — it is managed by the factory.
 */
export type TypedMutationOptions<TData, TVariables> = Omit<
  UseMutationOptions<TData, Error, TVariables>,
  'mutationFn'
>;

// ---------------------------------------------------------------------------
// PATCH mutation type utilities
// ---------------------------------------------------------------------------

/**
 * Extracts the request body JSON type for a given PATCH path.
 * Falls back to `never` when the path has no typed request body.
 */
type PatchRequestBody<P extends keyof paths> = paths[P] extends {
  patch: { requestBody?: { content: { 'application/json': infer T } } };
}
  ? T
  : never;

/**
 * Extracts the 200-response JSON body type for a given PATCH path.
 * Falls back to `unknown` when the path has no typed response.
 */
type PatchResponseData<P extends keyof paths> = paths[P] extends {
  patch: { responses: { 200: { content: { 'application/json': infer T } } } };
}
  ? T
  : unknown;

/**
 * Creates a typed useMutation hook for a POST endpoint.
 *
 * @param path    - The OpenAPI path key (e.g. '/api/auth/login/')
 * @param options - Optional extra UseMutationOptions (onSuccess, onError, etc.)
 *
 * @example
 *   export const useLoginMutation = createPostMutation('/api/auth/login/');
 */
export function createPostMutation<P extends keyof paths>(
  path: P,
  options?: TypedMutationOptions<PostResponseData<P>, PostRequestBody<P>>,
) {
  type TData = PostResponseData<P>;
  type TVariables = PostRequestBody<P>;

  return function useTypedMutation(extraOptions?: TypedMutationOptions<TData, TVariables>) {
    return useMutation<TData, Error, TVariables>({
      mutationFn: async (body: TVariables): Promise<TData> => {
        const result = await (
          apiClient.POST as (
            p: string,
            opts: { body: TVariables },
          ) => Promise<{ data?: unknown; error?: unknown; response: Response }>
        )(path as string, { body });
        if (result.error !== undefined) {
          const err = result.error;
          const detail =
            typeof err === 'object' && err !== null && 'detail' in err
              ? String((err as Record<string, unknown>)['detail'])
              : `API error: ${result.response.status}`;
          throw new Error(detail);
        }
        return result.data as TData;
      },
      ...options,
      ...extraOptions,
    });
  };
}

// ---------------------------------------------------------------------------
// PATCH mutation helper
// ---------------------------------------------------------------------------

/**
 * Creates a typed useMutation hook for a PATCH endpoint.
 *
 * @param path    - The OpenAPI path key (e.g. '/api/auth/profile/')
 * @param options - Optional extra UseMutationOptions (onSuccess, onError, etc.)
 *
 * @example
 *   export const useUpdateProfileMutation = createPatchMutation('/api/auth/profile/');
 */
export function createPatchMutation<P extends keyof paths>(
  path: P,
  options?: TypedMutationOptions<PatchResponseData<P>, PatchRequestBody<P>>,
) {
  type TData = PatchResponseData<P>;
  type TVariables = PatchRequestBody<P>;

  return function useTypedPatchMutation(extraOptions?: TypedMutationOptions<TData, TVariables>) {
    return useMutation<TData, Error, TVariables>({
      mutationFn: async (body: TVariables): Promise<TData> => {
        const result = await (
          apiClient.PATCH as (
            p: string,
            opts: { body: TVariables },
          ) => Promise<{ data?: unknown; error?: unknown; response: Response }>
        )(path as string, { body });
        if (result.error !== undefined) {
          const err = result.error;
          const detail =
            typeof err === 'object' && err !== null && 'detail' in err
              ? String((err as Record<string, unknown>)['detail'])
              : `API error: ${result.response.status}`;
          throw new Error(detail);
        }
        return result.data as TData;
      },
      ...options,
      ...extraOptions,
    });
  };
}
